/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import {
    getFilterElementsDefault,
    parseToInt,
    useErrorState,
    useWorkplaceState,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addTariffProcedureParam,
    changeTariffProcedureParam,
    deleteTariffProcedureParam,
    getSelectList,
    getTariffProcedureParam,
    getTariffProcedureParamsExcel,
    getTariffProcedureParamsList,
} from 'axios/tariffProcedureParamApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { Procedure } from 'models/api/procedure';
import {
    TariffProcedureParamsFilter as FilterType,
    TariffProcedureParam,
    TariffProcedureParamAdd,
    TariffProcedureParamChange,
    TariffProcedureParamTable,
} from 'models/api/tariffProcedureParam';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { deleteActionBtnFormatter } from 'utils/columnFormatters';
import TariffProcedureParamAddButton from './TariffProcedureParamAddButton';
import TariffProcedureParamsChangeModal from './TariffProcedureParamChangeModal';
import TariffProcedureParamFilter from './TariffProcedureParamsFilter';

const TariffProcedureParams = () => {
    const { t } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [procedures, setProcedures] = useState<Procedure[]>([]);
    const { handleRequestError } = useErrorState();
    const { hasRole } = useAuth();

    const { isFetching } = useQuery('tariffProcedures', () => getSelectList(api), {
        onSuccess: (data) => setProcedures(data),
        onError: (e) => handleRequestError(e),
    });

    return (
        <BOWorkplace<
            TariffProcedureParamTable,
            TariffProcedureParam,
            FilterType,
            TariffProcedureParamChange,
            TariffProcedureParamAdd
        >
            testId="tariff-procedure-params"
            pageTitle="BackOfficeParamSetsForProcedures"
            filterParams={{
                name: '',
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey="BackOfficeId" />,
                    width: 70,
                    minWidth: 70,
                },
                { key: 'name', name: <Trans i18nKey="BackOfficeName" /> },
                { key: 'procedureName', name: <Trans i18nKey="BackOfficeProcedureName" /> },
                { key: 'procedureDbName', name: <Trans i18nKey="BackOfficeProcedureDbName" /> },
                { key: 'dsc', name: <Trans i18nKey="BackOfficeDescription" /> },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    notHidden: true,
                    visible: hasRole('tariffProcedureParamsDelete'),
                    formatter: deleteActionBtnFormatter(hasRole('tariffProcedureParamsDelete')),
                },
            ]}
            storagePath="tariffprocedureparams/columns"
            getFilterElements={getFilterElementsDefault([
                {
                    id: 'procedureId',
                    formatter: (val) => {
                        const p = procedures.find((p) => p.id === parseToInt(val.toString()));
                        return `${t('BackOfficeProcedureDbName')}: ${p ? p.name : val.toString()}`;
                    },
                },
                { id: 'name', name: t('BackOfficeName') },
            ])}
            showModal={
                hasRole('tariffProcedureParamsGetById') ? () => setIsModalVisible(true) : undefined
            }
            buttons={
                hasRole('tariffProcedureParamsCreate')
                    ? [
                          <TariffProcedureParamAddButton
                              key="1"
                              showModal={() => setIsModalVisible(true)}
                          />,
                      ]
                    : []
            }
            filter={<TariffProcedureParamFilter procedures={procedures} />}
            modal={{
                component: isModalVisible && (
                    <TariffProcedureParamsChangeModal
                        isOpen={isModalVisible}
                        toggle={setIsModalVisible}
                        procedures={procedures}
                        isLoading={isFetching}
                    />
                ),
                fetchCurrentItemQueryFn: (id) => getTariffProcedureParam(api, id),
                changeMutationFn: (item) => changeTariffProcedureParam(api, item),
                addMutationFn: (item) => addTariffProcedureParam(api, item),
            }}
            deleteMutationFn={(id) => deleteTariffProcedureParam(api, id)}
            fetchQueryFn={(filter) => getTariffProcedureParamsList(api, filter)}
            queryKey={['tariffprocedureparams']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getTariffProcedureParamsExcel(api, filter)}
            loadingFilter={isFetching}
            showExcelBtn={hasRole('tariffProcedureParamsExcel')}
        />
    );
};

const useTariffProcedureParamsWorkplaceState = () =>
    useWorkplaceState<
        TariffProcedureParamTable,
        TariffProcedureParam,
        TariffProcedureParamChange,
        TariffProcedureParamAdd
    >();

export default TariffProcedureParams;
export { useTariffProcedureParamsWorkplaceState };
